import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Save,
  Cloud,
  Thermometer,
  Wind,
  Gauge,
  AlertCircle,
  X,
} from "lucide-react";
import { Session, WeatherCondition, TrackCondition, TrackConditions } from "@/types";
import { WindDirectionInput } from "./WindDirectionInput";

interface ValidationError {
  field: string;
  message: string;
}

interface SessionConditionsProps {
  session: Session;
  onSave: (conditions: TrackConditions) => void;
  isSaving?: boolean;
  error?: {
    message?: string;
    details?: Array<{
      code: string;
      path: (string | number)[];
      message: string;
    }>;
  } | null;
  className?: string;
}

const weatherConditionOptions: WeatherCondition[] = [
  "sunny",
  "partly_cloudy",
  "cloudy",
  "overcast",
  "light_rain",
  "heavy_rain",
  "drizzle",
  "fog",
  "mist",
  "snow",
  "hail",
  "thunderstorm",
];

const trackConditionOptions: TrackCondition[] = [
  "dry",
  "damp",
  "wet",
  "flooded",
  "icy",
  "snow_covered",
];

export const SessionConditions: React.FC<SessionConditionsProps> = ({
  session,
  onSave,
  isSaving = false,
  error,
  className = "",
}) => {
  const { t } = useTranslation(["pages", "common"]);
  const trackConditions = session.trackConditions || {};
  const [conditions, setConditions] = useState<TrackConditions>({
    airTemperature: trackConditions.airTemperature,
    humidity: trackConditions.humidity,
    windSpeed: trackConditions.windSpeed,
    windDirection: trackConditions.windDirection,
    barometricPressure: trackConditions.barometricPressure,
    weatherCondition: trackConditions.weatherCondition,
    surfaceTemperature: trackConditions.surfaceTemperature,
    trackCondition: trackConditions.trackCondition,
    trackGrip: trackConditions.trackGrip,
    conditionsNotes: trackConditions.conditionsNotes,
  });

  const [hasChanges, setHasChanges] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    [],
  );

  // Reset internal state when the incoming session prop changes
  useEffect(() => {
    const tc = session.trackConditions || {};
    setConditions({
      airTemperature: tc.airTemperature,
      humidity: tc.humidity,
      windSpeed: tc.windSpeed,
      windDirection: tc.windDirection,
      barometricPressure: tc.barometricPressure,
      weatherCondition: tc.weatherCondition,
      surfaceTemperature: tc.surfaceTemperature,
      trackCondition: tc.trackCondition,
      trackGrip: tc.trackGrip,
      conditionsNotes: tc.conditionsNotes,
    });
  }, [session]);

  // Parse validation errors from API response
  useEffect(() => {
    if (error?.details) {
      const errors = error.details.map((detail) => ({
        field: detail.path[0] as string,
        message: detail.message,
      }));
      setValidationErrors(errors);
    } else {
      setValidationErrors([]);
    }
  }, [error]);

  useEffect(() => {
    // Check if current conditions differ from session data
    const tc = session.trackConditions || {};
    const hasChanges = Object.keys(conditions).some((key) => {
      const conditionKey = key as keyof TrackConditions;
      return conditions[conditionKey] !== tc[conditionKey];
    });
    setHasChanges(hasChanges);
  }, [conditions, session]);

  const updateCondition = <K extends keyof TrackConditions>(
    field: K,
    value: TrackConditions[K],
  ) => {
    setConditions((prev: TrackConditions) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = () => {
    onSave(conditions);
  };

  const hasFieldError = (fieldName: string) => {
    return validationErrors.some((error) => error.field === fieldName);
  };

  const getWeatherIcon = (condition?: WeatherCondition) => {
    switch (condition) {
      case "sunny":
        return "☀️";
      case "partly_cloudy":
        return "⛅";
      case "cloudy":
      case "overcast":
        return "☁️";
      case "light_rain":
      case "drizzle":
        return "🌦️";
      case "heavy_rain":
        return "🌧️";
      case "thunderstorm":
        return "⛈️";
      case "fog":
      case "mist":
        return "🌫️";
      case "snow":
        return "❄️";
      case "hail":
        return "🧊";
      default:
        return "🌤️";
    }
  };

  const getFieldLabel = (field: string) => {
    const labels: Record<string, string> = {
      airTemperature: t("sessions.detail.conditions.fields.airTemperature", { ns: "pages" }),
      humidity: t("sessions.detail.conditions.fields.humidity", { ns: "pages" }),
      windSpeed: t("sessions.detail.conditions.fields.windSpeed", { ns: "pages" }),
      windDirection: t("sessions.detail.conditions.fields.windDirection", { ns: "pages" }),
      barometricPressure: t("sessions.detail.conditions.fields.barometricPressure", { ns: "pages" }),
      weatherCondition: t("sessions.detail.conditions.fields.weatherCondition", { ns: "pages" }),
      surfaceTemperature: t("sessions.detail.conditions.fields.surfaceTemperature", { ns: "pages" }),
      trackCondition: t("sessions.detail.conditions.fields.trackCondition", { ns: "pages" }),
      trackGrip: t("sessions.detail.conditions.fields.trackGrip", { ns: "pages" }),
      conditionsNotes: t("sessions.detail.conditions.fields.additionalNotes", { ns: "pages" }),
    };

    return labels[field] ?? field.replace(/([A-Z])/g, " $1").toLowerCase();
  };

  return (
 <div className={`bg-white rounded-lg border border-secondary-200 ${className}`}>
 <div className="border-b border-secondary-200 p-4">
 <div className="flex items-center justify-end space-x-2">
          {hasChanges && (
 <span className="text-xs text-amber-600 bg-amber-50 px-4 py-2 rounded">
              {t("sessions.detail.conditions.unsavedChanges", { ns: "pages" })}
            </span>
          )}
          <button
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
 className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2"
          >
 <Save className="h-4 w-4" />
            {isSaving ? t("sessions.detail.conditions.saving", { ns: "pages" }) : t("common:save")}
          </button>
        </div>
      </div>

      {/* Error Display */}
      {(error?.message || validationErrors.length > 0) && (
 <div className="border-b border-secondary-200 p-4 bg-red-50">
 <div className="flex items-start space-x-3">
 <AlertCircle className="h-5 w-5 text-red-400 mt-0.5 flex-shrink-0" />
 <div className="flex-1">
 <h4 className="text-sm font-medium text-red-800">
                {error?.message || t("sessions.detail.conditions.validationError", { ns: "pages" })}
              </h4>
              {validationErrors.length > 0 && (
 <div className="mt-2 space-y-1">
                  {validationErrors.map((error, index) => (
 <p key={index} className="text-sm text-red-700">
 <span className="font-medium">{getFieldLabel(error.field)}</span>
                      : {error.message}
                    </p>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={() => setValidationErrors([])}
 className="text-red-400 hover:text-red-600"
            >
 <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

 <div className="p-4">
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Weather Conditions */}
 <div className="space-y-4">
 <div className="flex items-center space-x-2 mb-3">
 <Cloud className="h-4 w-4 text-primary-500" />
 <h4 className="font-medium text-secondary-900">{t("sessions.detail.conditions.weatherTitle", { ns: "pages" })}</h4>
            </div>

 <div className="space-y-4">
              {/* Basic Weather Inputs */}
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
 <label className="block text-sm font-medium text-secondary-700 mb-1">
                    {t("sessions.detail.conditions.fields.weatherCondition", { ns: "pages" })}
                  </label>
 <div className="relative">
                    <select
                      value={conditions.weatherCondition || ""}
                      onChange={(e) =>
                        updateCondition(
                          "weatherCondition",
                          (e.target.value as WeatherCondition) || undefined,
                        )
                      }
 className={`form-select ${conditions.weatherCondition ? "pl-10" : ""} ${hasFieldError("weatherCondition") ? "border-red-300 focus:border-red-500 focus:ring-red-500" : ""}`}
                    >
                      <option value="">{t("sessions.detail.conditions.selectCondition", { ns: "pages" })}</option>
                      {weatherConditionOptions.map((option) => (
                        <option key={option} value={option}>
                          {t(`sessions.detail.conditions.weatherOptions.${option}`, { ns: "pages" })}
                        </option>
                      ))}
                    </select>
                    {conditions.weatherCondition && (
 <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-lg pointer-events-none">
                        {getWeatherIcon(conditions.weatherCondition)}
                      </div>
                    )}
                  </div>
                </div>

                <div>
 <label className="block text-sm font-medium text-secondary-700 mb-1">
                    {t("sessions.detail.conditions.fields.airTemperature", { ns: "pages" })}
                  </label>
 <div className="relative">
 <Thermometer className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
                    <input
                      type="number"
                      value={conditions.airTemperature || ""}
                      onChange={(e) =>
                        updateCondition(
                          "airTemperature",
                          e.target.value ? Number(e.target.value) : undefined,
                        )
                      }
 className={`form-input pl-10 ${hasFieldError("airTemperature") ? "border-red-300 focus:border-red-500 focus:ring-red-500" : ""}`}
                      placeholder="25"
                      step="0.1"
                    />
                  </div>
                </div>

                <div>
 <label className="block text-sm font-medium text-secondary-700 mb-1">
                    {t("sessions.detail.conditions.fields.humidity", { ns: "pages" })}
                  </label>
                  <input
                    type="number"
                    value={conditions.humidity || ""}
                    onChange={(e) =>
                      updateCondition(
                        "humidity",
                        e.target.value ? Number(e.target.value) : undefined,
                      )
                    }
 className="form-input"
                    placeholder="65"
                    min="0"
                    max="100"
                  />
                </div>

                <div>
 <label className="block text-sm font-medium text-secondary-700 mb-1">
                    {t("sessions.detail.conditions.fields.barometricPressure", { ns: "pages" })}
                  </label>
                  <input
                    type="number"
                    value={conditions.barometricPressure || ""}
                    onChange={(e) =>
                      updateCondition(
                        "barometricPressure",
                        e.target.value ? Number(e.target.value) : undefined,
                      )
                    }
 className="form-input"
                    placeholder="1013"
                    step="0.1"
                  />
                </div>

                <div>
 <label className="block text-sm font-medium text-secondary-700 mb-1">
                    {t("sessions.detail.conditions.fields.windSpeed", { ns: "pages" })}
                  </label>
 <div className="relative">
 <Wind className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
                    <input
                      type="number"
                      value={conditions.windSpeed || ""}
                      onChange={(e) =>
                        updateCondition(
                          "windSpeed",
                          e.target.value ? Number(e.target.value) : undefined,
                        )
                      }
 className="form-input pl-10"
                      placeholder="15"
                      min="0"
                    />
                  </div>
                </div>
              </div>

              {/* Wind Direction - Full Width for Compass */}
              <div>
                <WindDirectionInput
                  value={conditions.windDirection}
                  onChange={(value) => updateCondition("windDirection", value)}
                />
              </div>
            </div>
          </div>

          {/* Track Conditions */}
 <div className="space-y-4">
 <div className="flex items-center space-x-2 mb-3">
 <Gauge className="h-4 w-4 text-orange-500" />
 <h4 className="font-medium text-secondary-900">{t("sessions.detail.conditions.trackTitle", { ns: "pages" })}</h4>
            </div>

 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
 <label className="block text-sm font-medium text-secondary-700 mb-1">
                  {t("sessions.detail.conditions.fields.surfaceTemperature", { ns: "pages" })}
                </label>
 <div className="relative">
 <Thermometer className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
                  <input
                    type="number"
                    value={conditions.surfaceTemperature || ""}
                    onChange={(e) =>
                      updateCondition(
                        "surfaceTemperature",
                        e.target.value ? Number(e.target.value) : undefined,
                      )
                    }
 className="form-input pl-10"
                    placeholder="35"
                    step="0.1"
                  />
                </div>
              </div>

              <div>
 <label className="block text-sm font-medium text-secondary-700 mb-1">
                  {t("sessions.detail.conditions.fields.trackCondition", { ns: "pages" })}
                </label>
                <select
                  value={conditions.trackCondition || ""}
                  onChange={(e) =>
                    updateCondition(
                      "trackCondition",
                      (e.target.value as TrackCondition) || undefined,
                    )
                  }
 className="form-select"
                >
                  <option value="">{t("sessions.detail.conditions.selectCondition", { ns: "pages" })}</option>
                  {trackConditionOptions.map((option) => (
                    <option key={option} value={option}>
                      {t(`sessions.detail.conditions.trackOptions.${option}`, { ns: "pages" })}
                    </option>
                  ))}
                </select>
              </div>

 <div className="sm:col-span-2">
 <label className="block text-sm font-medium text-secondary-700 mb-1">
                  {t("sessions.detail.conditions.fields.trackGrip", { ns: "pages" })}
                </label>
 <div className="space-y-2">
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={conditions.trackGrip || 5}
                    onChange={(e) =>
                      updateCondition("trackGrip", Number(e.target.value))
                    }
 className="w-full h-2 bg-secondary-200 rounded-lg appearance-none cursor-pointer"
                  />
 <div className="flex justify-between text-xs text-secondary-500">
                    <span>{t("sessions.detail.conditions.trackGripLow", { ns: "pages" })}</span>
 <span className="font-medium text-secondary-700">
                      {conditions.trackGrip || 5}/10
                    </span>
                    <span>{t("sessions.detail.conditions.trackGripHigh", { ns: "pages" })}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Notes Section */}
 <div className="mt-6">
 <label className="block text-sm font-medium text-secondary-700 mb-2">
            {t("sessions.detail.conditions.fields.additionalNotes", { ns: "pages" })}
          </label>
          <textarea
            value={conditions.conditionsNotes || ""}
            onChange={(e) =>
              updateCondition("conditionsNotes", e.target.value || undefined)
            }
 className="form-textarea w-full"
            rows={3}
            placeholder={t("sessions.detail.conditions.notesPlaceholder", { ns: "pages" })}
          />
        </div>

        {/* Summary Cards */}
        {(conditions.airTemperature ||
          conditions.surfaceTemperature ||
          conditions.weatherCondition) && (
 <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {conditions.airTemperature && (
 <div className="bg-primary-50 border border-primary-200 rounded-lg p-3">
 <div className="flex items-center space-x-2">
 <Thermometer className="h-4 w-4 text-primary-600" />
 <span className="text-sm font-medium text-primary-900">
                    {t("sessions.detail.conditions.summary.airTemp", { ns: "pages" })}
                  </span>
                </div>
 <p className="text-lg font-bold text-primary-900">
                  {conditions.airTemperature}°C
                </p>
              </div>
            )}

            {conditions.surfaceTemperature && (
 <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
 <div className="flex items-center space-x-2">
 <Gauge className="h-4 w-4 text-orange-600" />
 <span className="text-sm font-medium text-orange-900">
                    {t("sessions.detail.conditions.summary.surfaceTemp", { ns: "pages" })}
                  </span>
                </div>
 <p className="text-lg font-bold text-orange-900">
                  {conditions.surfaceTemperature}°C
                </p>
              </div>
            )}

            {conditions.weatherCondition && (
 <div className="bg-green-50 border border-green-200 rounded-lg p-3">
 <div className="flex items-center space-x-2">
 <Cloud className="h-4 w-4 text-green-600" />
 <span className="text-sm font-medium text-green-900">
                    {t("sessions.detail.conditions.summary.weather", { ns: "pages" })}
                  </span>
                </div>
 <p className="text-sm font-bold text-green-900 capitalize">
                  {t(`sessions.detail.conditions.weatherOptions.${conditions.weatherCondition}`, { ns: "pages" })}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
